import { ProgressCard } from "../common/ProgressCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Clock3, CloudUpload, HelpCircle } from "lucide-react";
import { useWizard, type DocumentMetadata } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { UploadSectionIcon } from "../common/UploadSectionIcon";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import type { ReactNode } from "react";
import { CurrencySelect } from "@/components/workflow/common/CurrencySelect";

interface CommunityContributionCardProps {
  onCollapse: () => void;
}

function FieldBlock({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold uppercase leading-4 tracking-[0.08em] text-slate-500">
        {label}
      </Label>
      {children}
      {hint ? <p className="text-xs leading-4 text-slate-500">{hint}</p> : null}
    </div>
  );
}


export function CommunityContributionCard({ onCollapse }: CommunityContributionCardProps) {
  const {
    communityContributionData,
    updateCommunityContributionData,
    communityContributionCompletion,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Community contribution data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Community Contribution"}
        description={"Describe your community engagement and social impact"}
        onClose={onCollapse}
        onSave={() => {
          void handleSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Social"}
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        <SectionCard
          header={
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2.5">

                  <h3 className="text-lg font-semibold leading-7 text-slate-900 sm:text-xl">
                    {"Community Programs & Partnerships"}
                  </h3>
                  <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
                </div>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  {"Describe key community initiatives and partnerships you support"}
                </p>
              </div>
            </div>
          }
        >
          <Textarea
            placeholder={"e.g. Scholarship program for local students, Skills training for youth, Micro-finance support, Educational infrastructure, Healthcare initiatives, Water/sanitation projects, etc."}
            className="form-input min-h-[192px] rounded-xl px-4 py-4 text-base leading-6"
            value={communityContributionData.communityPrograms}
            onChange={(e) =>
              updateCommunityContributionData({ communityPrograms: e.target.value })
            }
            disabled={disabled}
          />
        </SectionCard>

        <div className="grid gap-6 lg:grid-cols-2">
          <SectionCard
            header={
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="text-lg font-semibold leading-7 text-slate-900 sm:text-xl">
                      {"Charitable Giving (Optional)"}
                    </h3>
                    <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
                  </div>
                  <p className="max-w-3xl text-sm leading-6 text-slate-600">
                    {"If you track charitable contributions, enter the amount"}
                  </p>
                </div>
              </div>
            }
          >
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_8rem]">
              <FieldBlock
                label={"Amount"}
                hint={"Reporting period amount"}
              >
                <Input
                  type="number"
                  placeholder={"e.g. 10000"}
                  step="0.01"
                  min="0"
                  value={communityContributionData.charitableGivingAmount}
                  onChange={(e) =>
                    updateCommunityContributionData({
                      charitableGivingAmount: e.target.value,
                    })
                  }
                  disabled={disabled}
                  className="form-input h-12 rounded-[10px] px-4 text-base"
                />
              </FieldBlock>

              <FieldBlock
                label={"Currency"}
              >
                <CurrencySelect
                  value={communityContributionData.charitableGivingCurrency}
                  onValueChange={(value) =>
                    updateCommunityContributionData({ charitableGivingCurrency: value })
                  }
                  disabled={disabled}
                  lowercase
                  triggerClassName="h-12 rounded-[10px] border-slate-200 bg-primary/10 text-sm text-slate-900 shadow-none focus:ring-slate-300"
                />
              </FieldBlock>
            </div>
          </SectionCard>

          <SectionCard
            header={
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="text-lg font-semibold leading-7 text-slate-900 sm:text-xl">
                      {"Employee Volunteer Hours (Optional)"}
                    </h3>
                    <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
                  </div>
                  <p className="max-w-3xl text-sm leading-6 text-slate-600">
                    {"If tracked, enter total volunteer hours for the reporting period"}
                  </p>
                </div>
              </div>
            }
          >
            <FieldBlock
              label={"Total volunteer hours"}
              hint={"Hours spent by employees on community initiatives"}
            >
              <div className="relative">
                <Clock3 className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-slate-700" />
                <Input
                  type="number"
                  placeholder={"e.g. 120"}
                  step="0.5"
                  min="0"
                  value={communityContributionData.volunteerHours}
                  onChange={(e) =>
                    updateCommunityContributionData({ volunteerHours: e.target.value })
                  }
                  disabled={disabled}
                  className="form-input h-12 rounded-[10px] pl-10 pr-4 text-base"
                />
              </div>
            </FieldBlock>
          </SectionCard>
        </div>

        <SectionCard
          header={
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="text-lg font-semibold leading-7 text-slate-900 sm:text-xl">
                    {"Measurable Outcomes (Optional)"}
                  </h3>
                  <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
                </div>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  {"Share any quantifiable impact or outcomes from your programs"}
                </p>
              </div>
            </div>
          }
        >
          <Textarea
            placeholder={"e.g. 500 students trained in agricultural skills, 50 microloans disbursed, 1000 people gained access to clean water..."}
            className="form-input min-h-[192px] rounded-xl px-4 py-4 text-base leading-6"
            value={communityContributionData.measurableOutcomes}
            onChange={(e) =>
              updateCommunityContributionData({ measurableOutcomes: e.target.value })
            }
            disabled={disabled}
          />
        </SectionCard>

        <SectionCard
          collapsible={true}
          header={
            <div className="flex items-center gap-3">
              <UploadSectionIcon />
              <h3 className="text-lg font-bold leading-7 text-slate-900">
                {"Supporting Evidence (Optional)"}
              </h3>
            </div>
          }
        >
          <DocumentUploadSection
            fieldIdentifier="social-community"
            documents={(communityContributionData.evidenceFiles || []) as DocumentMetadata[]}
            onDocumentsChange={(docs: DocumentMetadata[]) =>
              updateCommunityContributionData({ evidenceFiles: docs as any })
            }
            currentTab="social"
            label={"Supporting Evidence (Optional)"}
            description={"Upload photos, community letters, program reports, or impact documentation"}
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
            maxFiles={10}
            borderColor="border-slate-200"
            hideHeader
            className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10"
            emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            documentsListClassName="space-y-3"
            itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
            actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            emptyStateContent={
              <div className="flex flex-col items-center text-center">
                <div className="pb-4">
                  <div className="rounded-full bg-slate-500/5 p-4">
                    <CloudUpload className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="text-base font-bold leading-6 text-slate-800">
                  Click to upload or drag and drop
                </p>
                <p className="pt-1 text-sm leading-5 text-slate-500">
                  {"Upload photos, community letters, program reports, or impact documentation"}
                </p>
              </div>
            }
          />
        </SectionCard>

        <ProgressCard completion={communityContributionCompletion} />
      </div>
    </div>
  );
}
